import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { FormikProps } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface AddressHistoryEntry {
  address: {
    line1: string;
    line2?: string;
    city: string;
    county?: string;
    postcode: string;
  };
  movedInDate: string;
  movedOutDate?: string;
  isAbroad?: boolean;
}

interface AddressHistorySectionProps {
  formik: FormikProps<any>;
}

const AddressHistorySection: React.FC<AddressHistorySectionProps> = ({ formik }) => {
  const addressHistory = formik.values.addressHistory || [];

  const addAddressEntry = () => {
    const newEntry: AddressHistoryEntry = {
      address: {
        line1: '',
        line2: '',
        city: '',
        county: '',
        postcode: ''
      },
      movedInDate: '',
      movedOutDate: '',
      isAbroad: false
    };
    formik.setFieldValue('addressHistory', [...addressHistory, newEntry]);
  };

  const removeAddressEntry = (index: number) => {
    const updated = addressHistory.filter((_: any, i: number) => i !== index);
    formik.setFieldValue('addressHistory', updated);
  };

  const updateAddressEntry = (index: number, field: string, value: any) => {
    const updated = [...addressHistory];
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      updated[index] = {
        ...updated[index],
        address: {
          ...updated[index].address,
          [addressField]: value
        }
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
    }
    formik.setFieldValue('addressHistory', updated);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'white' }}>
          5-Year Address History
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={addAddressEntry}
          variant="outlined"
          size="small"
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main',
            '&:hover': {
              borderColor: 'primary.light',
              backgroundColor: 'rgba(25, 118, 210, 0.08)'
            }
          }}
        >
          Add Previous Address
        </Button>
      </Box>
      
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
        Please provide all addresses where you have lived in the last 5 years. If you lived abroad, check the "Lived Abroad" box.
      </Typography>

      {addressHistory.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', my: 2 }}>
          No previous addresses added. Click "Add Previous Address" to add your address history.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {addressHistory.map((entry: AddressHistoryEntry, index: number) => (
            <Grid item xs={12} key={index}>
              <Box sx={{
                p: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                position: 'relative'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: 'white' }}>
                    Previous Address {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeAddressEntry(index)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={entry.isAbroad || false}
                          onChange={(e) => updateAddressEntry(index, 'isAbroad', e.target.checked)}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        />
                      }
                      label="Lived Abroad (outside UK)"
                      sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                    />
                  </Grid>

                  {!entry.isAbroad && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Address Line 1"
                          value={entry.address?.line1 || ''}
                          onChange={(e) => updateAddressEntry(index, 'address.line1', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            '& .MuiInputBase-input': { color: 'white' }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Address Line 2"
                          value={entry.address?.line2 || ''}
                          onChange={(e) => updateAddressEntry(index, 'address.line2', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            '& .MuiInputBase-input': { color: 'white' }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="City/Town"
                          value={entry.address?.city || ''}
                          onChange={(e) => updateAddressEntry(index, 'address.city', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            '& .MuiInputBase-input': { color: 'white' }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="County"
                          value={entry.address?.county || ''}
                          onChange={(e) => updateAddressEntry(index, 'address.county', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            '& .MuiInputBase-input': { color: 'white' }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Postcode"
                          value={entry.address?.postcode || ''}
                          onChange={(e) => updateAddressEntry(index, 'address.postcode', e.target.value.toUpperCase())}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            '& .MuiInputBase-input': { color: 'white' }
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Moved In Date"
                        value={entry.movedInDate ? new Date(entry.movedInDate) : null}
                        onChange={(newValue) => updateAddressEntry(index, 'movedInDate', newValue ? newValue.toISOString() : '')}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                              },
                              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                              '& .MuiInputBase-input': { color: 'white' }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'rgba(25, 25, 25, 0.95)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                '& .MuiPickersDay-root': {
                                  color: 'white',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                  '&.Mui-selected': { backgroundColor: 'primary.main' }
                                },
                                '& .MuiPickersCalendarHeader-root': { color: 'white' },
                                '& .MuiPickersArrowSwitcher-button': { color: 'white' },
                                '& .MuiDayCalendar-weekDayLabel': { color: 'rgba(255, 255, 255, 0.7)' }
                              }
                            }
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Moved Out Date (optional)"
                        value={entry.movedOutDate ? new Date(entry.movedOutDate) : null}
                        onChange={(newValue) => updateAddressEntry(index, 'movedOutDate', newValue ? newValue.toISOString() : '')}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                              },
                              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                              '& .MuiInputBase-input': { color: 'white' }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'rgba(25, 25, 25, 0.95)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                '& .MuiPickersDay-root': {
                                  color: 'white',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                  '&.Mui-selected': { backgroundColor: 'primary.main' }
                                },
                                '& .MuiPickersCalendarHeader-root': { color: 'white' },
                                '& .MuiPickersArrowSwitcher-button': { color: 'white' },
                                '& .MuiDayCalendar-weekDayLabel': { color: 'rgba(255, 255, 255, 0.7)' }
                              }
                            }
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AddressHistorySection;
